// Turning a template's proposed settings changes into something a person can actually read.
//
// The plan reports each change as a field name and two raw values, which is fine for a color and useless for
// the seven *Config fields, whose values are whole JSON documents. Rendered as-is they came out as
// `Home Page Config: {"card":{"borderRadius":4},"navPodBand":{"backgro…` — technically the change, and no help
// at all in deciding whether to take it.
//
// So two jobs live here, both pure. `diffConfigBlob` walks a pair of those documents and reports the leaf
// properties that actually differ, by their own names (`card.borderRadius`, `navPodBand.backgroundColor`).
// `describeChangeValue` says what KIND of thing a value is, so the caller can draw a swatch for a color, a
// sample in the actual typeface for a font, a thumbnail for an image, and plain text for the rest.
//
// Neither knows anything about the storefront runtime; the classification is by property name and value
// shape, which is all the config blobs ever carry. Where the runtime's own conventions matter they are
// followed: a number inside a style bag is px, and `fontSize` is a PERCENT rather than px (textStyleFrom
// appends '%'), so showing it as pixels would misreport every font size a template changes.

/** What a value is, which decides how it should be drawn. */
export type ChangeValueKind = 'absent' | 'color' | 'font' | 'image' | 'number' | 'boolean' | 'text' | 'token';

export interface IChangeValue {
    kind: ChangeValueKind;
    /** For 'token', the slot it names — 'menuBar', 'heading' — so it can be read as a word. */
    slot?: string;
    /** The value as a person should read it: '#f58220', '1200px', '120%', 'on', 'Poppins'. */
    text: string;
    /** For 'color' and 'image', the raw value the caller needs to draw the swatch or the thumbnail. */
    raw?: string;
}

/** One leaf property that differs between a template's blob and the storefront's. */
export interface IBlobChange {
    /** The property's own dotted name, e.g. `card.borderRadius`. */
    path: string;
    from: IChangeValue;
    to: IChangeValue;
}

// The settings fields whose value is a JSON document rather than a scalar.
const CONFIG_FIELD_SUFFIX = 'Config';

/** True for a settings field whose value is a JSON configuration document. */
export const isConfigBlobField = (field: string): boolean => field.endsWith(CONFIG_FIELD_SUFFIX);

// Property names that carry a color, beyond anything whose value looks like one outright.
const COLOR_NAME = /color$|^color$/i;
// Property names that carry a typeface.
const FONT_NAME = /^font$|fontfamily$|^(header|default)font$/i;
// Property names that carry an image URL.
const IMAGE_NAME = /logo$|favicon$|imageurl$|iconurl$|sourceurl$|^image$/i;
// Property names whose number is a CSS length, so it reads as px. Deliberately a list rather than "every
// number": a count or a z-index shown as "3px" is worse than showing it bare.
const LENGTH_NAME = /width|height|padding|margin|radius|gap|spacing|top$|bottom$|left$|right$|size$|offset/i;

// A design token rather than a value: the storefront resolves `$palette:menuBar` against whichever palette is
// active and `$font:heading` against whichever font pack is. Token-built templates carry most of their look
// this way, so showing the raw string would report the majority of a change as gibberish — and resolving it
// here is not possible either, because it resolves against the palette the apply is ABOUT to install.
const PALETTE_TOKEN = /^\$palette:([A-Za-z0-9_-]+)/;
const FONT_TOKEN = /^\$font:([A-Za-z0-9_-]+)/;

const COLOR_VALUE = /^(#[0-9a-f]{3,8}|rgb|rgba|hsl|hsla|transparent$|currentcolor$)/i;
const IMAGE_VALUE = /^(https?:)?\/\/.+\.(png|jpe?g|gif|webp|svg|avif)(\?|$)/i;

const leafName = (path: string): string => path.split('.').pop() ?? path;

// 'menuBarText' -> 'menu bar text'. A token's slot is the only part of it worth reading.
const humanizeSlot = (slot: string): string =>
    slot.replace(/([a-z0-9])([A-Z])/g, '$1 $2').replace(/[-_]/g, ' ').toLowerCase();

/**
 * What kind of thing a value is, and how it should read. `path` is the property's name — the top-level
 * settings field for a scalar, or the dotted path inside a config document for a leaf.
 */
export const describeChangeValue = (path: string, value: unknown): IChangeValue => {
    if (value === null || value === undefined || value === '') return { kind: 'absent', text: 'not set' };

    const name = leafName(path);

    if (typeof value === 'boolean') return { kind: 'boolean', text: value ? 'on' : 'off' };

    if (typeof value === 'number') {
        // A style bag's numbers are px, except fontSize, which the renderer emits as a percent.
        if (/^fontsize$/i.test(name)) return { kind: 'number', text: `${value}%` };
        if (LENGTH_NAME.test(name)) return { kind: 'number', text: `${value}px` };
        return { kind: 'number', text: String(value) };
    }

    if (typeof value === 'string') {
        const trimmed = value.trim();
        const palette = PALETTE_TOKEN.exec(trimmed);
        if (palette) return { kind: 'token', text: `${humanizeSlot(palette[1])} from the palette`, slot: palette[1] };
        const font = FONT_TOKEN.exec(trimmed);
        if (font) return { kind: 'token', text: `${humanizeSlot(font[1])} from the font pack`, slot: font[1] };
        if (COLOR_NAME.test(name) || COLOR_VALUE.test(trimmed)) {
            return { kind: 'color', text: trimmed, raw: trimmed };
        }
        if (IMAGE_NAME.test(name) || IMAGE_VALUE.test(trimmed)) {
            return { kind: 'image', text: fileNameOf(trimmed), raw: trimmed };
        }
        if (FONT_NAME.test(name)) return { kind: 'font', text: trimmed, raw: trimmed };
        return { kind: 'text', text: truncate(trimmed) };
    }

    // An array or object that survived to here is a leaf the diff chose not to walk into — describe its
    // shape rather than dumping it, which is the whole point of this module.
    if (Array.isArray(value)) {
        return { kind: 'text', text: `${value.length} ${value.length === 1 ? 'item' : 'items'}` };
    }
    return { kind: 'text', text: truncate(JSON.stringify(value)) };
};

/**
 * The leaf properties that differ between a template's configuration document and the storefront's. Values
 * that are equal are left out, so a blob carrying fifty identical properties and one new color reports the
 * one color.
 *
 * Arrays are compared whole rather than per index: a reordered list of tiles is one change to a person, not
 * five. Anything unparsable on either side is treated as absent, because a malformed blob is a thing stored
 * data contains and must not break a screen.
 */
export const diffConfigBlob = (from: unknown, to: unknown): IBlobChange[] => {
    const before = asObject(from);
    const after = asObject(to);
    const changes: IBlobChange[] = [];

    const walk = (a: Record<string, unknown>, b: Record<string, unknown>, prefix: string) => {
        const keys = Array.from(new Set([...Object.keys(a), ...Object.keys(b)])).sort();
        for (const key of keys) {
            const path = prefix ? `${prefix}.${key}` : key;
            const left = a[key];
            const right = b[key];
            // Recurse whenever EITHER side is a group of properties, treating the other as empty. A template
            // that ADDS a whole style group (the storefront has no navPodBand at all) otherwise reported the
            // group as one blob of JSON — the same unreadable row this module exists to replace, one level
            // down. Only a genuine type change (a group where there was a scalar) falls through to a
            // single row, because there is no property-by-property story to tell about it.
            const bothSidesAreGroupsOrAbsent =
                (isPlainObject(left) || left === undefined)
                && (isPlainObject(right) || right === undefined)
                && (isPlainObject(left) || isPlainObject(right));
            if (bothSidesAreGroupsOrAbsent) {
                walk(isPlainObject(left) ? left : {}, isPlainObject(right) ? right : {}, path);
                continue;
            }
            if (JSON.stringify(left ?? null) === JSON.stringify(right ?? null)) continue;
            changes.push({
                path,
                from: describeChangeValue(path, left),
                to: describeChangeValue(path, right),
            });
        }
    };

    walk(before, after, '');
    return changes;
};

/** Parses a blob into an object, or an empty one. Never throws: stored blobs include malformed values. */
const asObject = (value: unknown): Record<string, unknown> => {
    if (isPlainObject(value)) return value;
    if (typeof value !== 'string' || value.trim() === '') return {};
    try {
        const parsed = JSON.parse(value);
        return isPlainObject(parsed) ? parsed : {};
    } catch {
        return {};
    }
};

const isPlainObject = (value: unknown): value is Record<string, unknown> =>
    typeof value === 'object' && value !== null && !Array.isArray(value);

// A blob URL's last segment, without its cache-buster — enough to tell two images apart in a row of text.
const fileNameOf = (url: string): string => {
    const withoutQuery = url.split('?')[0];
    return withoutQuery.split('/').pop() || withoutQuery;
};

const truncate = (text: string, max = 40): string => (text.length > max ? `${text.slice(0, max)}…` : text);

// ---- Grouping, so a template changing forty things does not arrive as a wall of forty rows ----

/** The grouping a settings field belongs to on the "what changes" step. */
export type ChangeSection = 'colors' | 'fonts' | 'sectionBackgrounds' | 'page' | 'other';

const COLOR_FIELDS = ['Theme', 'MenuBarColor', 'MenuBarTextColor', 'BackgroundColor', 'TextColor'];
const FONT_FIELDS = ['HeaderFont', 'DefaultFont'];
const SECTION_BACKGROUND_FIELDS = [
    'SectionBackgroundColor', 'SectionHeaderBackgroundColor', 'SectionFooterBackgroundColor',
];

export const CHANGE_SECTION_LABELS: Record<ChangeSection, string> = {
    colors: 'Theme and colors',
    fonts: 'Fonts',
    sectionBackgrounds: 'Section backgrounds',
    page: 'Page styling',
    other: 'Other settings',
};

/**
 * Which group a field belongs under. A configuration document is its OWN group — each one carries its own
 * pile of properties, so collapsing them separately is what keeps the step readable — while the handful of
 * scalars collapse together by what they affect.
 */
export const changeSectionOf = (field: string): ChangeSection => {
    if (isConfigBlobField(field)) return 'page';
    if (COLOR_FIELDS.includes(field)) return 'colors';
    if (FONT_FIELDS.includes(field)) return 'fonts';
    if (SECTION_BACKGROUND_FIELDS.includes(field)) return 'sectionBackgrounds';
    return 'other';
};

/**
 * How many individual things a change represents: one for a scalar, and for a configuration document the
 * number of leaf properties that actually differ. Used for the count on a collapsed group, so the count
 * matches what expanding it shows.
 */
export const changeSize = (field: string, from: unknown, to: unknown): number =>
    isConfigBlobField(field) ? diffConfigBlob(from, to).length : 1;
